import React, { useEffect } from "react";
import { PrimeReactProvider } from "primereact/api";
import { HelmetProvider } from "react-helmet-async";
import { useUnit } from "effector-react";
import "@/core/styles/globals.scss";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { $authStore, initializeAuth } from "@/features/auth/store/auth";
import ErrorBoundary from "@/core/components/errorBoundary/ErrorBoundary";
import AppRouter from "@/core/routes/AppRouter";
import { ToastManager } from "@/core/components/toastManager/ToastManager";
import Tracking from "@/core/utils/tracking";

import { fetchNotificationsRequested } from "@/features/notifications/store";
import {
  fetchConversationHistoryRequested,
  fetchConversationsRequested,
} from "@/features/chat/store/chatStore";
import {
  MercureProvider,
  useMercureMessage,
} from "@/core/services/mercure/mercureContext";

/**
 * Component to handle Mercure real-time messages
 * This component must be INSIDE MercureProvider
 */
const MercureMessageHandler: React.FC = () => {
  // Listen to new notification events
  useMercureMessage("new_notification", (data) => {
    //console.log("🔔 New notification received via Mercure:", data);

    // Delay refetch by 5 seconds to allow backend processing
    setTimeout(() => {
      //console.log("🔄 Refetching notifications");
      fetchNotificationsRequested();
    }, 5000);
  });

  // Listen to new chat message events
  useMercureMessage("new_message", (data) => {
    //console.log("💬 New chat message received via Mercure:", data);

    if (!data) {
      console.warn("Received new_message event with no data");
      return;
    }

    // Refetch conversations for inbox badge update
    fetchConversationsRequested();

    // Refetch the specific conversation if conversation_id is present
    const conversationId: string | undefined = data.conversation_id?.toString();

    if (conversationId) {
      //console.log(`🔄 Refetching conversation history for ${conversationId}`);
      fetchConversationHistoryRequested(conversationId);
    } else {
      console.warn("new_message event missing conversation_id:", data);
    }
  });

  return null;
};

/**
 * Wrapper component that conditionally renders MercureProvider
 * Only connects to Mercure when user is authenticated
 */
const AppWithMercure: React.FC = () => {
  const authState = useUnit($authStore);
  const { isAuthenticated, user } = authState;

  // Build Mercure topic from user ID
  const topic = user?.uid ? `user/${user.uid}/events` : "";

  return (
    <div className="App">
      <ErrorBoundary
        fallback={<div>Critical application error - please refresh</div>}
      >
        {isAuthenticated && topic ? (
          <MercureProvider
            topic={topic}
            hubUrl="https://api.eucalls.net/mercure/.well-known/mercure"
            enabled={true}
          >
            <MercureMessageHandler />
            <AppRouter />
          </MercureProvider>
        ) : (
          <AppRouter />
        )}
      </ErrorBoundary>
      <ToastManager />
    </div>
  );
};

const App: React.FC = () => {
  // Initialize auth on app startup
  useEffect(() => {
    //console.log("App component mounted, initializing auth...");
    initializeAuth();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      Tracking.initialize();
    }, 3000);
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <PrimeReactProvider>
          <AppWithMercure />
        </PrimeReactProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
